import React, { FC, useState } from 'react';
import PopUp from 'components/PopUps/PopUp';
import AddCalculationScript, {
  SaveCalculationData,
} from 'components/CalculationScripts/PopUps/AddCalculationScript/AddCalculationScript';
import AddSimpleOutputs from 'components/SimpleOutputs/PopUps/AddSimpleOutputs';
import RuleBuilder from 'components/RuleBuilder';
import { ModuleType } from 'DecisionStrategy/DecisionStrategiesTypes';
import {
  RulePopUpType,
  SimpleOutputRequestData,
  CalculationScriptData,
  RuleRequestData,
  EntityType,
} from 'RuleCreate/Types';
import { getSerializedRules } from 'containers/EditRulePopUpContainer/utils';
import { RuleData } from 'BranchInfo/Types';
import { CalculationScriptMode } from 'api/Types';

interface EditRulePopUpProps {
  moduleType: ModuleType | null;
  ruleType: RulePopUpType | null;
  entityType: EntityType | null;
  currentRule: RuleData;
  currentCondition: RuleData;
  closePopUp: () => void;
  editSimpleOutput: (data: SimpleOutputRequestData) => void;
  editCalculationScript: (data: CalculationScriptData) => void;
  editRule: (data: RuleRequestData) => void;
  openVariableCreation: () => void;
  calculationMode?: CalculationScriptMode;
}

const getTitleByModuleType = (moduleType: ModuleType) => {
  switch (moduleType) {
    case 'requirements':
      return 'Edit Requirements Rule';
    case 'output':
      return 'Edit Rule-Based Output';
    case 'calculations':
      return 'Edit Calculation Script';
    case 'scorecard':
      return 'Edit Scoring Rule';
    case 'assignments':
      return 'Edit Output';
    default:
      return '';
  }
};

const EditRulePopUp: FC<EditRulePopUpProps> = ({
  moduleType,
  ruleType,
  currentRule,
  currentCondition,
  entityType,
  closePopUp,
  editSimpleOutput,
  editRule,
  editCalculationScript,
  openVariableCreation,
  calculationMode,
}) => {
  const [isSaving, setIsSaving] = useState(false);

  const currentRuleByEntityType = entityType === 'branch' ? currentCondition : currentRule;
  const { serializedRules, conditionOutput } = getSerializedRules(currentRuleByEntityType);

  const title = entityType === 'branch' ? 'Edit Branch Condition' : getTitleByModuleType(moduleType as ModuleType);

  const handleEditRule = async (data: RuleRequestData) => {
    try {
      setIsSaving(true);
      await editRule(data);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditCalculationScript = async (data: SaveCalculationData) => {
    try {
      setIsSaving(true);

      await editCalculationScript({
        propertyAttribute: data.variableId,
        comparisonValue: data.code,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditSimpleOutputs = async (data: SimpleOutputRequestData) => {
    try {
      setIsSaving(true);

      await editSimpleOutput(data);
    } finally {
      setIsSaving(false);
    }
  };

  const getContent = () => {
    if (
      moduleType === 'requirements' ||
      moduleType === 'output' ||
      moduleType === 'scorecard' ||
      entityType === 'branch'
    ) {
      return (
        <RuleBuilder
          saveRule={handleEditRule}
          type={ruleType as RulePopUpType}
          multipleOutputs={moduleType === 'output'}
          withoutDeclineReason={entityType === 'branch'}
          moduleType={moduleType as ModuleType}
          serializedRules={serializedRules}
          serializedOutputs={entityType === 'branch' ? [] : conditionOutput}
          isEdit
          openVariableCreation={openVariableCreation}
          isSaving={isSaving}
        />
      );
    }
    if (moduleType === 'calculations') {
      const [rule] = serializedRules;

      return (
        <AddCalculationScript
          calculation={rule?.firstOperandValue}
          sourceVariableId={rule?.sourceVariableId}
          saveCalculationScript={handleEditCalculationScript}
          openVariableCreation={openVariableCreation}
          isSaving={isSaving}
          mode={calculationMode || 'javascript'}
        />
      );
    }
    if (moduleType === 'assignments') {
      return (
        <AddSimpleOutputs
          saveSimpleOutput={handleEditSimpleOutputs}
          serializedRules={serializedRules}
          serializedOutputs={conditionOutput}
          openVariableCreation={openVariableCreation}
          isSaving={isSaving}
        />
      );
    }
    return null;
  };

  const renderTitleTooltip = () => {
    if (moduleType !== 'calculations') {
      return null;
    }

    return (
      <>
        <p>This variable will automatically calculate the</p>
        <p>calculation you provide. Reference other</p>
        <p>variables using their system name and return a</p>
        <p>single value</p>
      </>
    );
  };

  return (
    <PopUp titleTooltip={renderTitleTooltip()} title={title} closePopUp={closePopUp}>
      {getContent()}
    </PopUp>
  );
};

export default EditRulePopUp;
